const STORAGE_KEY = 'ops.continuationAlarmEnabled';
const MAX_ALARM_MS = 20_000;
const REPEAT_MS = 2_000;

type BrowserAudioContext = AudioContext;
export type ContinuationAlarmStatus = 'disabled' | 'locked' | 'ready' | 'unsupported' | 'blocked';

function hasStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function createAudioContext(): BrowserAudioContext | null {
  if (typeof window === 'undefined') return null;
  const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  return AudioContextCtor ? new AudioContextCtor() : null;
}

function hasAudioSupport() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
}

export function isContinuationAlarmEnabled() {
  if (!hasStorage()) return false;
  return window.localStorage.getItem(STORAGE_KEY) === '1';
}

export function setContinuationAlarmEnabled(enabled: boolean) {
  if (!hasStorage()) return;
  if (enabled) window.localStorage.setItem(STORAGE_KEY, '1');
  else window.localStorage.removeItem(STORAGE_KEY);
}

class ContinuationAlarm {
  private audioContext: BrowserAudioContext | null = null;
  private delayId: number | null = null;
  private intervalId: number | null = null;
  private timeoutId: number | null = null;
  private activePromptId: string | null = null;

  getStatus(): ContinuationAlarmStatus {
    if (!isContinuationAlarmEnabled()) return 'disabled';
    const context = this.audioContext;
    if (!context) return hasAudioSupport() ? 'locked' : 'unsupported';
    if (context.state === 'running') return 'ready';
    if (context.state === 'closed') return 'blocked';
    return 'locked';
  }

  canPlayNow() {
    return this.audioContext?.state === 'running';
  }

  async unlock() {
    const context = this.ensureAudioContext();
    if (!context) return false;
    if (context.state === 'suspended') await context.resume();
    if (context.state !== 'running') return false;
    this.playTestTone();
    setContinuationAlarmEnabled(true);
    return true;
  }

  async start(promptId: string, options: { delayMs?: number } = {}) {
    if (!isContinuationAlarmEnabled()) return false;
    if (this.activePromptId === promptId) return true;
    this.stop();
    this.activePromptId = promptId;
    const ready = await this.ensureReady();
    if (!ready) {
      this.stop();
      return false;
    }
    const delayMs = Math.max(0, options.delayMs ?? 0);
    if (delayMs > 0) {
      this.delayId = window.setTimeout(() => this.startRinging(), delayMs);
    } else {
      this.startRinging();
    }
    return true;
  }

  stop() {
    if (this.delayId !== null) window.clearTimeout(this.delayId);
    if (this.intervalId !== null) window.clearInterval(this.intervalId);
    if (this.timeoutId !== null) window.clearTimeout(this.timeoutId);
    this.delayId = null;
    this.intervalId = null;
    this.timeoutId = null;
    this.activePromptId = null;
  }

  private async ensureReady() {
    const context = this.ensureAudioContext();
    if (!context) return false;
    if (context.state === 'suspended') {
      await context.resume();
    }
    return context.state === 'running';
  }

  private ensureAudioContext() {
    if (!this.audioContext) this.audioContext = createAudioContext();
    return this.audioContext;
  }

  private playPattern() {
    this.playTone(0, 880, 0.18, 0.22);
    this.playTone(0.22, 660, 0.2, 0.24);
  }

  private playTestTone() {
    this.playTone(0, 760, 0.16, 0.16);
    this.playTone(0.18, 960, 0.12, 0.14);
  }

  private startRinging() {
    if (!this.activePromptId || this.intervalId !== null) return;
    this.delayId = null;
    this.playPattern();
    this.intervalId = window.setInterval(() => this.playPattern(), REPEAT_MS);
    this.timeoutId = window.setTimeout(() => this.stop(), MAX_ALARM_MS);
  }

  private playTone(delaySeconds: number, frequency: number, durationSeconds: number, peakVolume: number) {
    const context = this.audioContext;
    if (!context) return;
    const startAt = context.currentTime + delaySeconds;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, startAt);
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(peakVolume, startAt + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSeconds);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + durationSeconds + 0.02);
  }
}

export const continuationAlarm = new ContinuationAlarm();
